import {
  applyStateSpecRequirements,
  getRequiredVehicleSpecFields,
} from './stateRequirements';
import { applyRegistrationMonthsRequirement } from './registrationMonthsField';

const validateVINFormat = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

const REQUIRED_FIELDS_FEES = [
  { key: "zipCode", label: "Zip Code" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "country", label: "County" },
  { key: "basePrice", label: "Base Price" },
  { key: "plateType", label: "Plate Type" },
  { key: "transactionType", label: "Transaction Type" },
  { key: "titleStatus", label: "Title Status" },
  { key: "vinCode", label: "Vin Code" },
  { key: "vehicleType", label: "Vehicle Type" },
  { key: "fuelType", label: "Fuel Type" },
  { key: "vehicleWeight", label: "Vehicle Weight" },
  { key: "msrp", label: "MSRP" },
  { key: "modelYear", label: "Model Year" }
];

const MPG_FIELD_LABELS = {
  cityMpg:     "MPG (City)",
  highwayMpg:  "Highway MPG",
  combinedMpg: "Combined MPG",
};

/**
 * Returns the set of Fees-form MPG field keys required by the supplied
 * state + vehicle type. Accepts either a full state name ("Oregon") or an
 * abbreviation ("OR"); returns an empty array when the state has no MPG
 * requirement or none has been selected yet.
 *
 * The per-state table now lives in `utility/stateRequirements.js`, ported
 * from the bot's `state_requirements.py` so both sides read the same
 * rules. `<MPG>` is a single value in the ATC spec and the Fees form
 * sends `MPG: data.cityMpg || data.mpg`, so City MPG is the field that
 * satisfies it; Highway and Combined MPG are not required by any state.
 *
 * Retained as a named export for backwards compatibility with existing
 * callers — new code should prefer `getRequiredVehicleSpecFields`, which
 * also covers GVW and Weight.
 */
export const getRequiredMpgFieldsForState = (stateValue, vehicleType) => {
  const { fields } = getRequiredVehicleSpecFields('fees', stateValue, vehicleType);
  return fields.filter(({ spec }) => spec === 'mpg').map(({ key }) => key);
};

export { MPG_FIELD_LABELS };

const REQUIRED_FIELDS_DOCS = [
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'country', label: 'County' },
  { key: 'vinCode', label: 'VIN' },
  { key: 'vehicleType', label: 'Vehicle Type' },
  { key: 'condition', label: 'Condition' },
  { key: 'transactionType', label: 'Transaction Type' },
  { key: 'transactionDate', label: 'Transaction Date' },
  { key: 'uploadDate', label: 'Upload Date' },
];

const FormValidation = (formType = '', formData = {}) => {
  const errors = {};

  switch (formType) {
    case "Title":
      const trimmedVin = formData.vinCode?.trim();

      if (!trimmedVin) {
        errors.vinCode = 'VIN is required';
      } else if (!validateVINFormat(trimmedVin)) {
        errors.vinCode = 'Please enter a valid 17-character VIN';
      }
      break;

    case "Fees":
      REQUIRED_FIELDS_FEES.forEach(({ key, label }) => {
        if (!formData[key]) {
          errors[key] = `${label} is required`;
        }
      });
      if (!(["Purchase", "Refinance"].includes(formData["transactionType"]))) {
        errors["transactionType"] = "Transaction Type must be either Purchase or Refinance";
      }
      // Conditional vehicle-specification validation, driven by the same
      // per-state + per-vehicle-type tables the bot uses (see
      // utility/stateRequirements.js): GVW, Vehicle Weight, GVWR, City MPG,
      // Combined MPG, Cylinders, Horsepower, Number of Tires, Passenger
      // Capacity, Engine CC, Length, Square Footage, Fuel Type, Model Year
      // and MSRP. Adds nothing when neither the selected state nor the
      // vehicle type asks for a given data point, and never overwrites a
      // message already set by REQUIRED_FIELDS_FEES above — Vehicle Weight,
      // Fuel Type, MSRP and Model Year are all unconditionally required on
      // this form, so in practice this adds the rest.
      applyStateSpecRequirements('fees', formData, errors);
      // Registration Months is required once a state is known. The Fees form
      // prefills it from the per-state table as soon as the ZIP decode lands,
      // so this only fires if the user cleared it — see
      // utility/registrationMonthsField.js.
      applyRegistrationMonthsRequirement(formData, errors);
      break;

    case "Docs":
      REQUIRED_FIELDS_DOCS.forEach(({ key, label }) => {
        if (!formData[key]) {
          errors[key] = `${label} is required`;
        }
      });
      // Same per-state and per-vehicle-type rules as Fees, mapped onto the
      // Docs / Refi-Docs field keys (single averaged `mpg` rather than
      // city/highway/combined) and narrowed to the data points the ATC Docs
      // service actually carries — Weight, GVW, MPG, Cylinders, Horsepower,
      // Number of Tires, Fuel Type, Model Year and MSRP. Docs and Refi-Docs
      // share this branch — see pages/docs/DocumentWrapper.js.
      applyStateSpecRequirements('docs', formData, errors);
      break;

    default:
      return null;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export default FormValidation;
